import React, { useState, useEffect } from 'react';
import './PathhManagement.css';
import AddCategoryForm from './AddCategoryForm';
import AddPathhForm from './AddPathhForm';
import PathhCategoryCard from './PathhCategoryCard';
import API_CONFIG from '../../config/apiConfig';

const PathhManagement = () => {
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showAddCategoryForm, setShowAddCategoryForm] = useState(false);
    const [showAddPathhForm, setShowAddPathhForm] = useState(false);
    const [categorySearchTerm, setCategorySearchTerm] = useState('');
    const [pathhSearchTerm, setPathhSearchTerm] = useState('');

    // Edit states
    const [editingCategory, setEditingCategory] = useState(null);
    const [editCategoryName, setEditCategoryName] = useState('');

    useEffect(() => {
        fetchCategories();
    }, []);

    const fetchCategories = async () => {
        try {
            setLoading(true);
            const apiUrl = API_CONFIG.getPathhCategoriesUrl();
            const response = await fetch(apiUrl);

            if (response.ok) {
                const data = await response.json();
                if (Array.isArray(data)) {
                    setCategories(data);
                } else if (data && Array.isArray(data.results)) {
                    setCategories(data.results);
                } else {
                    setCategories([]);
                }
                setError(null);
            } else {
                const errorText = await response.text();
                setError(`API Error (${API_CONFIG.CURRENT_ENV}): ${response.status} - ${errorText}`);
                setCategories([]);
            }
        } catch (err) {
            setError(`Network Error (${API_CONFIG.CURRENT_ENV}): ${err.message}`);
            setCategories([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchCategoryDetails = async (categoryId) => {
        try {
            const response = await fetch(`https://pooja-path-1.onrender.com/api/pathh/categories/${categoryId}/`);
            if (response.ok) {
                const data = await response.json();
                setSelectedCategory(data);
            }
        } catch (err) {
            console.error('Error fetching category details:', err);
        }
    };

    const handleDeleteCategory = async (categoryId) => {
        if (!window.confirm('Are you sure you want to delete this category?')) return;
        try {
            const response = await fetch(`https://pooja-path-1.onrender.com/api/pathh/categories/${categoryId}/delete/`, { method: 'DELETE' });
            if (response.ok) {
                alert('Category deleted successfully');
                await fetchCategories();
                if (selectedCategory?.id === categoryId) setSelectedCategory(null);
            } else {
                const errorData = await response.json();
                alert(`Failed to delete category: ${errorData.error || 'Unknown error'}`);
            }
        } catch (err) {
            alert('Failed to delete category');
        }
    };

    const handleEditCategory = (category) => {
        setEditingCategory(category);
        setEditCategoryName(category.name);
    };

    const handleCancelEditCategory = () => {
        setEditingCategory(null);
        setEditCategoryName('');
    };

    const handleSaveEditCategory = async () => {
        try {
            const response = await fetch(`https://pooja-path-1.onrender.com/api/pathh/categories/${editingCategory.id}/update/`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ name: editCategoryName })
            });
            if (response.ok) {
                alert('Category updated successfully');
                setEditingCategory(null);
                setEditCategoryName('');
                await fetchCategories();
                if (selectedCategory?.id === editingCategory.id) {
                    setSelectedCategory({ ...selectedCategory, name: editCategoryName });
                }
            } else {
                alert('Failed to update category');
            }
        } catch (err) {
            alert('Error updating category');
        }
    };

    const handleDeletePathh = async (categoryId, pathhTitle) => {
        if (!window.confirm('Are you sure you want to delete this pathh?')) return;
        try {
            const encodedTitle = encodeURIComponent(pathhTitle);
            const response = await fetch(`https://pooja-path-1.onrender.com/api/pathh/categories/${categoryId}/pathhs/${encodedTitle}/delete/`, { method: 'DELETE' });
            if (response.ok) {
                alert('Pathh deleted successfully');
                await fetchCategoryDetails(categoryId);
            } else {
                const errorData = await response.json();
                alert(`Failed to delete pathh: ${errorData.error || 'Unknown error'}`);
            }
        } catch (err) {
            alert('Failed to delete pathh');
        }
    };

    const handleEditPathh = async (categoryId, oldTitle, newTitle, newText) => {
        try {
            const encodedTitle = encodeURIComponent(oldTitle);
            const response = await fetch(`https://pooja-path-1.onrender.com/api/pathh/categories/${categoryId}/pathhs/${encodedTitle}/update/`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ title: newTitle, pathh_text: newText })
            });
            if (response.ok) {
                alert('Pathh updated successfully');
                await fetchCategoryDetails(categoryId);
            } else {
                alert('Failed to update pathh');
            }
        } catch (err) {
            alert('Error updating pathh');
        }
    };

    const handleCategoryAdded = () => { setShowAddCategoryForm(false); fetchCategories(); };
    const handlePathhAdded = () => { setShowAddPathhForm(false); selectedCategory && fetchCategoryDetails(selectedCategory.id); };

    const filteredCategories = categories.filter(category => category.name.toLowerCase().includes(categorySearchTerm.toLowerCase()));
    const filteredPathhs = selectedCategory?.pathhs?.filter(pathh => pathh.title.toLowerCase().includes(pathhSearchTerm.toLowerCase()) || (pathh.pathh_text && pathh.pathh_text.toLowerCase().includes(pathhSearchTerm.toLowerCase()))) || [];

    if (loading) return <div className="pathh-management"><div className="loading-message">Loading pathh categories...</div></div>;

    return (
        <div className="pathh-management">
            {/* Edit Category Modal */}
            {editingCategory && (
                <div className="edit-modal-overlay">
                    <div className="edit-modal">
                        <h2>✏️ Edit Category</h2>
                        <div className="edit-form">
                            <label>Category Name:</label>
                            <input
                                type="text"
                                value={editCategoryName}
                                onChange={(e) => setEditCategoryName(e.target.value)}
                            />
                            <div className="edit-modal-buttons">
                                <button className="save-button" onClick={handleSaveEditCategory}>💾 Save</button>
                                <button className="cancel-button" onClick={handleCancelEditCategory}>❌ Cancel</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="management-header">
                <h2 className="section-title">Pathh Management</h2>
                <p className="section-description">Manage pathh categories and their content</p>
                <div className="connection-status">
                    {error ? <span className="status-error">API Connection Issue: {error}</span> : <span className="status-success">Connected to Database</span>}
                </div>
                <button className="add-button" onClick={() => setShowAddCategoryForm(true)}>+ Add New Category</button>
            </div>

            <div className="pathh-layout">
                <div className="categories-section">
                    <h3>Categories ({categories.length})</h3>
                    <div className="search-bar-container">
                        <input type="text" placeholder="Search categories..." value={categorySearchTerm} onChange={(e) => setCategorySearchTerm(e.target.value)} className="search-input" />
                    </div>
                    <div className="categories-grid">
                        {filteredCategories.length === 0 ? (
                            <div className="empty-state">
                                <div className="empty-icon">📖</div>
                                <h3>{categorySearchTerm ? 'No Matching Categories Found' : 'No Categories Found'}</h3>
                                <p>{categorySearchTerm ? 'Try adjusting your search term.' : 'Create your first pathh category to get started.'}</p>
                            </div>
                        ) : (
                            filteredCategories.map(category => (
                                <div key={category.id} className={`category-card ${selectedCategory?.id === category.id ? 'active' : ''}`} onClick={() => fetchCategoryDetails(category.id)}>
                                    <div className="category-header">
                                        <h4>{category.name}</h4>
                                        <div className="category-actions">
                                            <button className="edit-btn" onClick={(e) => { e.stopPropagation(); handleEditCategory(category); }} title="Edit category">✏️</button>
                                            <button className="delete-btn" onClick={(e) => { e.stopPropagation(); handleDeleteCategory(category.id); }} title="Delete category">🗑️</button>
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>

                {selectedCategory && (
                    <div className="category-details">
                        <div className="details-header">
                            <h3>{selectedCategory.name}</h3>
                            <button className="add-pathh-btn" onClick={() => setShowAddPathhForm(true)}>+ Add Pathh</button>
                        </div>

                        <div className="pathhs-list">
                            <h4>Pathhs ({selectedCategory.pathhs?.length || 0})</h4>
                            <div className="search-bar-container">
                                <input type="text" placeholder="Search pathhs..." value={pathhSearchTerm} onChange={(e) => setPathhSearchTerm(e.target.value)} className="search-input" />
                            </div>
                            {filteredPathhs.length === 0 && selectedCategory.pathhs?.length === 0 ? (
                                <div className="empty-pathhs"><p>No pathhs in this category yet.</p></div>
                            ) : filteredPathhs.length === 0 ? (
                                <div className="empty-pathhs"><p>No matching pathhs found.</p></div>
                            ) : (
                                <div className="pathhs-grid">
                                    {filteredPathhs.map((pathh, index) => (
                                        <PathhCategoryCard
                                            key={index}
                                            pathh={pathh}
                                            categoryId={selectedCategory.id}
                                            onDelete={handleDeletePathh}
                                            onEdit={handleEditPathh}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {showAddCategoryForm && <AddCategoryForm onClose={() => setShowAddCategoryForm(false)} onCategoryAdded={handleCategoryAdded} />}
            {showAddPathhForm && selectedCategory && <AddPathhForm categoryId={selectedCategory.id} categoryName={selectedCategory.name} onClose={() => setShowAddPathhForm(false)} onPathhAdded={handlePathhAdded} />}
        </div>
    );
};

export default PathhManagement;
